import {useCallback, useRef, useState} from 'react'
import {Button, Form} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {PersonalLeadOneLead} from '../leadTypes'

import {
  AddressComponent,
  GooglePlacesAutocompleteInput,
  PlaceSelection,
} from '../../../components/GooglePlacesAutocompleteInput'
import {AddressSearchModal} from '../../../components/AddressSearchModal'

const formMessages = defineMessages({
  streetAddress: {id: 'leads.form.streetAddress', defaultMessage: 'Street address'},
  city: {id: 'common.field.city', defaultMessage: 'City'},
  state: {id: 'common.field.state', defaultMessage: 'State'},
  zipCode: {id: 'common.field.zipCode', defaultMessage: 'Zip code'},
  searchAddress: {id: 'leads.form.searchAddress', defaultMessage: 'Search address'},
})

export const FormPersonalLead = ({info}: {info?: PersonalLeadOneLead | null}) => {
  const intl = useIntl()
  const cityRef = useRef<HTMLInputElement | null>(null)
  const countryRef = useRef<HTMLInputElement | null>(null)
  const stateRef = useRef<HTMLInputElement | null>(null)
  const postalCodeRef = useRef<HTMLInputElement | null>(null)
  const streetAddressRef = useRef<HTMLInputElement | null>(null)
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false)
  const [modalInitialAddress, setModalInitialAddress] = useState<string>('')

  const getAddressPart = useCallback(
    (components: AddressComponent[] | undefined, types: string[]) => {
      if (!components || components.length === 0) return ''
      const component = components.find((item) => types.every((type) => item.types.includes(type)))
      return component?.long_name ?? ''
    },
    []
  )

  const handlePlaceSelect = useCallback(
    ({addressComponents, address}: PlaceSelection) => {
      if (address && streetAddressRef.current) {
        streetAddressRef.current.value = address
      }

      if (!addressComponents) return

      const city =
        getAddressPart(addressComponents, ['locality']) ||
        getAddressPart(addressComponents, ['postal_town']) ||
        getAddressPart(addressComponents, ['administrative_area_level_2'])
      const state = getAddressPart(addressComponents, ['administrative_area_level_1'])
      const country = getAddressPart(addressComponents, ['country'])
      const postalCode = getAddressPart(addressComponents, ['postal_code'])

      if (cityRef.current) cityRef.current.value = city || ''
      if (stateRef.current) stateRef.current.value = state || ''
      if (countryRef.current) countryRef.current.value = country || ''
      if (postalCodeRef.current) postalCodeRef.current.value = postalCode || ''
    },
    [getAddressPart]
  )

  const openAddressModal = useCallback(() => {
    const currentValue = streetAddressRef.current?.value ?? info?.streetAddress ?? ''
    setModalInitialAddress(currentValue)
    setIsAddressModalOpen(true)
  }, [info?.streetAddress])

  const closeAddressModal = useCallback(() => {
    setIsAddressModalOpen(false)
  }, [])

  return (
    <>
      <Form.Group className='mb-3'>
        <Form.Label>
          <FormattedMessage id='leads.form.streetAddress' defaultMessage='Street address' />
        </Form.Label>
        <GooglePlacesAutocompleteInput
          name='streetAddress'
          type='text'
          placeholder={intl.formatMessage(formMessages.streetAddress)}
          defaultValue={info?.streetAddress || undefined}
          onPlaceSelect={handlePlaceSelect}
          ref={streetAddressRef}
        />
        <Button
          variant='primary'
          size='sm'
          className='mt-2'
          type='button'
          onClick={openAddressModal}
        >
          <FormattedMessage id='leads.form.searchAddress' defaultMessage='Search address' />
        </Button>
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>
          <FormattedMessage id='common.field.city' defaultMessage='City' />
        </Form.Label>
        <Form.Control
          name='city'
          type='text'
          placeholder={intl.formatMessage(formMessages.city)}
          defaultValue={info?.city || undefined}
          ref={cityRef}
        />
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>
          <FormattedMessage id='common.field.state' defaultMessage='State' />
        </Form.Label>
        <Form.Control
          type='text'
          name='state'
          placeholder={intl.formatMessage(formMessages.state)}
          defaultValue={info?.state || undefined}
          ref={stateRef}
        />
      </Form.Group>
      <Form.Group className='mb-3'>
        <Form.Label>
          <FormattedMessage id='common.field.zipCode' defaultMessage='Zip code' />
        </Form.Label>
        <Form.Control
          name='postalCode'
          type='text'
          placeholder={intl.formatMessage(formMessages.zipCode)}
          defaultValue={info?.postalCode || undefined}
          ref={postalCodeRef}
        />
      </Form.Group>
      {/* Hidden Country field - filled by address search */}
      <input
        type='hidden'
        name='country'
        defaultValue={info?.country || undefined}
        ref={countryRef}
      />

      <AddressSearchModal
        show={isAddressModalOpen}
        onClose={closeAddressModal}
        onSelect={handlePlaceSelect}
        initialAddress={modalInitialAddress}
        modalTitle={intl.formatMessage(formMessages.searchAddress)}
      />
    </>
  )
}
